import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { Box, IconButton, useTheme, type SxProps } from "@mui/material";
import useEmblaCarousel from "embla-carousel-react";
import { type ElementType } from "react";
import { imageURL } from "../../http";
import { useDotButton, usePrevNextButtons } from "../../hooks";
import { Placeholder } from "./placeholder";

export interface Image {
    src: string;
    title: string;
    color?: [number, number, number];
}

export interface CarouselProps {
    items: Image[];
    sx?: SxProps;
    container?: ElementType;
}

export const Carousel = ({ items, container: Container = Box, sx = {} }: CarouselProps): JSX.Element => {
    const [emblaRef, emblaApi] = useEmblaCarousel({ startIndex: 0 });
    const { selectedIndex } = useDotButton(emblaApi);
    const { onPrevButtonClick, onNextButtonClick } = usePrevNextButtons(emblaApi);
    const t = useTheme();
    return (
        <Box sx={{ position: "relative", overflow: "hidden" }} ref={emblaRef}>
            <Box sx={{ display: "flex" }}>
                {items.map((x, i) => (
                    <Container
                        aria-label={x.title}
                        key={x.src}
                        sx={{
                            flex: "0 0 100%",
                            minWidth: 0,
                            minHeight: 235,
                            textAlign: "center",
                            padding: 2,
                            // touchAction: "pan-y pinch-zoom",
                        }}
                        draggable={false}
                    >
                        {x.src ? <Box
                            component="img"
                            src={imageURL(x.src)}
                            fetchPriority={i === 0 ? "high" : "low"}
                            sx={{
                                maxWidth: "100%",
                                objectFit: "contain",
                                ...sx,
                            }}
                            crossOrigin="anonymous"
                            loading={i === 0 ? "eager" : "lazy"}
                            alt={x.title}
                        /> : <Placeholder sx={{ color: t.palette.primary.light }} />}
                    </Container>
                ))}
            </Box>
            {items.length > 1 && (
                <>
                    <Box sx={{ position: "absolute", top: "40%", left: 0 }}>
                        <IconButton aria-label="Cycle Previous" disabled={selectedIndex === 0} onClick={onPrevButtonClick}>
                            <ChevronLeft />
                        </IconButton>
                    </Box>
                    <Box sx={{ position: "absolute", top: "40%", right: 0 }}>
                        <IconButton aria-label="Cycle Next" disabled={selectedIndex === items.length - 1} onClick={onNextButtonClick}>
                            <ChevronRight />
                        </IconButton>
                    </Box>
                </>
            )}
            {items.length > 1 ? <Pagination dots={items} index={selectedIndex} /> : <></>}
        </Box>
    );
};

const Pagination = ({ dots, index }: { dots: Image[]; index: number }) => {
    return (
        <Box
            sx={{
                position: "absolute",
                width: "100%",
                bottom: 8,
                display: "flex",
                flexDirection: "row",
            }}
        >
            <Box sx={{ flex: 1 }} />
            {dots.map((x, i) => (
                <Box
                    key={x.src}
                    sx={t => ({
                        backgroundColor: index === i ? t.palette.primary.main : "gray",
                        height: 8,
                        width: 8,
                        borderRadius: 2,
                        margin: 1,
                    })}
                />
            ))}
            <Box sx={{ flex: 1 }} />
        </Box>
    );
};
